// The AI team in the client's seven sections (one per agent layer): each shows
// as one card with one set of numbers and jobs. Agents stay separate behind it.
import type { TeamAgent } from '@/lib/ai-team'
import {
  Crown,
  Headset,
  type LucideIcon,
  Megaphone,
  Rocket,
  Scale,
  ShieldCheck,
  Wallet
} from 'lucide-react'

export type Tone =
  | 'emerald'
  | 'amber'
  | 'red'
  | 'sky'
  | 'violet'
  | 'indigo'
  | 'rose'
  | 'teal'
  | 'fuchsia'
  | 'neutral'

export interface Hub {
  id: string
  title: string
  subtitle: string
  icon: LucideIcon
  tone: Tone
  layers: string[]
}

// The client's blueprint: seven sections, in this order.
export const HUBS: Hub[] = [
  {
    id: 'executive',
    title: 'Executive',
    subtitle: 'Chief of Staff: your briefs and your queue',
    icon: Crown,
    tone: 'rose',
    layers: ['executive']
  },
  {
    id: 'growth',
    title: 'Growth & Acquisition',
    subtitle: 'Paid ads and new merchants',
    icon: Rocket,
    tone: 'violet',
    layers: ['growth']
  },
  {
    id: 'content',
    title: 'Content & Marketing',
    subtitle: 'Posts, newsletters and ad creative',
    icon: Megaphone,
    tone: 'sky',
    layers: ['content']
  },
  {
    id: 'finops',
    title: 'Financial Operations',
    subtitle: 'Payments, payouts, refunds and chargebacks',
    icon: Wallet,
    tone: 'emerald',
    layers: ['finops']
  },
  {
    id: 'support',
    title: 'Support & Operations',
    subtitle: 'Members, orders, bookings and merchants',
    icon: Headset,
    tone: 'indigo',
    layers: ['support']
  },
  {
    id: 'safety',
    title: 'Infrastructure & Safety',
    subtitle: 'Moderation and listing accuracy',
    icon: ShieldCheck,
    tone: 'teal',
    layers: ['safety']
  },
  {
    id: 'legal',
    title: 'Legal, Compliance & Trust',
    subtitle: 'Agreements, copyright and privacy requests',
    icon: Scale,
    tone: 'fuchsia',
    layers: ['legal']
  }
]

export const hubAgents = (hub: Hub, agents: TeamAgent[]) =>
  agents.filter((a) => hub.layers.includes(a.layer))

/** Numbers for a set of agents: a hub, or the whole team. */
export function totals(agents: TeamAgent[]) {
  const sum = (pick: (a: TeamAgent) => number) => agents.reduce((n, a) => n + pick(a), 0)
  return {
    waiting: sum((a) => a.waiting),
    working: sum((a) => a.working),
    runs: sum((a) => a.runs_today),
    failed: sum((a) => a.failed_today),
    emails: sum((a) => a.open_threads),
    spent: sum((a) => Number(a.spent_today)),
    budget: sum((a) => (a.enabled ? Number(a.daily_budget_usd) : 0))
  }
}

// Colour carries meaning: emerald working/healthy, amber waiting for you,
// red failed/money/urgent, neutral off. Hub tones only colour icon chips.
export const CHIP: Record<Tone, string> = {
  emerald: 'bg-emerald-500/10 text-emerald-600 ring-1 ring-emerald-500/20 dark:text-emerald-400',
  amber: 'bg-amber-500/10 text-amber-600 ring-1 ring-amber-500/20 dark:text-amber-400',
  red: 'bg-red-500/10 text-red-600 ring-1 ring-red-500/20 dark:text-red-400',
  sky: 'bg-sky-500/10 text-sky-600 ring-1 ring-sky-500/20 dark:text-sky-400',
  violet: 'bg-violet-500/10 text-violet-600 ring-1 ring-violet-500/20 dark:text-violet-400',
  indigo: 'bg-indigo-500/10 text-indigo-600 ring-1 ring-indigo-500/20 dark:text-indigo-400',
  rose: 'bg-rose-500/10 text-rose-600 ring-1 ring-rose-500/20 dark:text-rose-400',
  teal: 'bg-teal-500/10 text-teal-600 ring-1 ring-teal-500/20 dark:text-teal-400',
  fuchsia: 'bg-fuchsia-500/10 text-fuchsia-600 ring-1 ring-fuchsia-500/20 dark:text-fuchsia-400',
  neutral: 'bg-neutral-100 text-neutral-600 ring-1 ring-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:ring-neutral-700'
}

export const card =
  'rounded-[36px] border border-neutral-200 bg-white p-5 dark:border-neutral-800 dark:bg-neutral-900'

export const money = (n: number) =>
  `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
